// Common form utilities

// Auto-format DOB as user types digits: MM/DD/YYYY
export function formatDateInput(raw) {
  if (!raw) return "";
  const digits = String(raw).replace(/\D/g, "").slice(0, 8);
  const len = digits.length;
  if (len === 0) return "";
  if (len <= 2) return digits;
  if (len <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
}

export function formatPhone(value) {
  if (!value) return "";
  const digits = String(value).replace(/\D/g, "").slice(0, 10);
  const len = digits.length;
  if (len === 0) return "";
  if (len < 4) return digits;
  if (len < 7) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
}

export function isValidEmail(value) {
  if (!value) return false;
  // Pragmatic email regex
  return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(String(value).trim());
}

export function formatDateISO(date) {
  if (!date) return "";
  if (typeof date === "string") return date;
  try {
    const d = new Date(date);
    if (Number.isNaN(d.getTime())) return "";
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  } catch {
    return "";
  }
}

export function parseDateLoose(s) {
  if (!s) return null;
  const cleaned = s.replace(/[^\d/-]/g, "");
  // Try MM/DD/YYYY or YYYY-MM-DD
  let d = null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(cleaned)) {
    d = new Date(cleaned + "T00:00:00");
  } else if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(cleaned)) {
    const [m, day, y] = cleaned.split("/").map(Number);
    d = new Date(y, m - 1, day);
  }
  return d && !Number.isNaN(d.getTime()) ? d : null;
}

export function formatHumanDate(date) {
  if (!date) return "";
  const d = typeof date === "string" ? parseDateLoose(date) : date;
  if (!d) return "";
  return d.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
}

// Comprehensive airport list — US commercial airports + major international
export const AIRPORTS = [
  // --- NEW YORK / TRI-STATE ---
  { code: "JFK", name: "New York JFK", city: "New York" },
  { code: "LGA", name: "New York LaGuardia", city: "New York" },
  { code: "EWR", name: "Newark Liberty", city: "Newark / New York" },
  { code: "HPN", name: "Westchester County", city: "White Plains, NY" },
  { code: "ISP", name: "Long Island MacArthur", city: "Islip, NY" },
  { code: "SWF", name: "New York Stewart", city: "Newburgh, NY" },
  // --- CALIFORNIA ---
  { code: "LAX", name: "Los Angeles", city: "Los Angeles" },
  { code: "SFO", name: "San Francisco", city: "San Francisco" },
  { code: "SAN", name: "San Diego", city: "San Diego" },
  { code: "OAK", name: "Oakland", city: "Oakland" },
  { code: "SJC", name: "San Jose Mineta", city: "San Jose" },
  { code: "SNA", name: "John Wayne / Orange County", city: "Santa Ana / OC" },
  { code: "BUR", name: "Hollywood Burbank", city: "Burbank" },
  { code: "LGB", name: "Long Beach", city: "Long Beach" },
  { code: "ONT", name: "Ontario", city: "Ontario, CA" },
  { code: "PSP", name: "Palm Springs", city: "Palm Springs" },
  { code: "SBA", name: "Santa Barbara", city: "Santa Barbara" },
  { code: "MRY", name: "Monterey", city: "Monterey" },
  { code: "FAT", name: "Fresno Yosemite", city: "Fresno" },
  { code: "SMF", name: "Sacramento", city: "Sacramento" },
  // --- ILLINOIS / MIDWEST HUBS ---
  { code: "ORD", name: "Chicago O'Hare", city: "Chicago" },
  { code: "MDW", name: "Chicago Midway", city: "Chicago" },
  { code: "DTW", name: "Detroit Metro", city: "Detroit" },
  { code: "MSP", name: "Minneapolis-St. Paul", city: "Minneapolis" },
  { code: "STL", name: "St. Louis Lambert", city: "St. Louis" },
  { code: "MCI", name: "Kansas City", city: "Kansas City" },
  { code: "MKE", name: "Milwaukee Mitchell", city: "Milwaukee" },
  { code: "IND", name: "Indianapolis", city: "Indianapolis" },
  { code: "CMH", name: "Columbus", city: "Columbus, OH" },
  { code: "CLE", name: "Cleveland Hopkins", city: "Cleveland" },
  { code: "CVG", name: "Cincinnati", city: "Cincinnati" },
  { code: "PIT", name: "Pittsburgh", city: "Pittsburgh" },
  { code: "OMA", name: "Omaha Eppley", city: "Omaha" },
  { code: "DSM", name: "Des Moines", city: "Des Moines" },
  { code: "FAR", name: "Fargo", city: "Fargo, ND" },
  { code: "SDF", name: "Louisville", city: "Louisville" },
  { code: "GRR", name: "Grand Rapids", city: "Grand Rapids" },
  { code: "MSN", name: "Madison", city: "Madison, WI" },
  // --- TEXAS ---
  { code: "DFW", name: "Dallas/Fort Worth", city: "Dallas" },
  { code: "DAL", name: "Dallas Love Field", city: "Dallas" },
  { code: "IAH", name: "Houston Intercontinental", city: "Houston" },
  { code: "HOU", name: "Houston Hobby", city: "Houston" },
  { code: "AUS", name: "Austin-Bergstrom", city: "Austin" },
  { code: "SAT", name: "San Antonio", city: "San Antonio" },
  { code: "ELP", name: "El Paso", city: "El Paso" },
  { code: "LBB", name: "Lubbock Preston Smith", city: "Lubbock" },
  { code: "MAF", name: "Midland", city: "Midland/Odessa" },
  // --- FLORIDA (fuller for FL Seller of Travel) ---
  { code: "MIA", name: "Miami", city: "Miami" },
  { code: "FLL", name: "Fort Lauderdale-Hollywood", city: "Fort Lauderdale" },
  { code: "MCO", name: "Orlando", city: "Orlando" },
  { code: "TPA", name: "Tampa", city: "Tampa" },
  { code: "SRQ", name: "Sarasota-Bradenton", city: "Sarasota" },
  { code: "JAX", name: "Jacksonville", city: "Jacksonville" },
  { code: "RSW", name: "Southwest Florida", city: "Fort Myers" },
  { code: "PBI", name: "Palm Beach International", city: "West Palm Beach" },
  { code: "PIE", name: "St. Pete-Clearwater", city: "St. Petersburg" },
  { code: "SFB", name: "Orlando Sanford", city: "Sanford / Orlando" },
  { code: "PGD", name: "Punta Gorda", city: "Punta Gorda" },
  { code: "PNS", name: "Pensacola", city: "Pensacola" },
  { code: "VPS", name: "Destin-Fort Walton Beach", city: "Destin" },
  { code: "ECP", name: "Northwest Florida Beaches", city: "Panama City Beach" },
  { code: "TLH", name: "Tallahassee", city: "Tallahassee" },
  { code: "GNV", name: "Gainesville", city: "Gainesville, FL" },
  { code: "DAB", name: "Daytona Beach", city: "Daytona Beach" },
  { code: "MLB", name: "Melbourne Orlando", city: "Melbourne, FL" },
  { code: "APF", name: "Naples", city: "Naples, FL" },
  { code: "EYW", name: "Key West", city: "Key West" },
  // --- SOUTHEAST / MID-ATLANTIC ---
  { code: "ATL", name: "Atlanta Hartsfield-Jackson", city: "Atlanta" },
  { code: "CLT", name: "Charlotte Douglas", city: "Charlotte" },
  { code: "RDU", name: "Raleigh-Durham", city: "Raleigh / Durham" },
  { code: "GSO", name: "Piedmont Triad", city: "Greensboro" },
  { code: "CHS", name: "Charleston", city: "Charleston, SC" },
  { code: "MYR", name: "Myrtle Beach", city: "Myrtle Beach" },
  { code: "SAV", name: "Savannah/Hilton Head", city: "Savannah" },
  { code: "GSP", name: "Greenville-Spartanburg", city: "Greenville, SC" },
  { code: "AVL", name: "Asheville Regional", city: "Asheville" },
  { code: "ILM", name: "Wilmington", city: "Wilmington, NC" },
  { code: "BNA", name: "Nashville", city: "Nashville" },
  { code: "MEM", name: "Memphis", city: "Memphis" },
  { code: "TYS", name: "Knoxville McGhee Tyson", city: "Knoxville" },
  { code: "TRI", name: "Tri-Cities", city: "Blountville, TN" },
  { code: "MSY", name: "New Orleans Louis Armstrong", city: "New Orleans" },
  { code: "BHM", name: "Birmingham-Shuttlesworth", city: "Birmingham" },
  { code: "HSV", name: "Huntsville", city: "Huntsville" },
  { code: "MOB", name: "Mobile Regional", city: "Mobile" },
  { code: "JAN", name: "Jackson-Medgar Wiley Evers", city: "Jackson, MS" },
  { code: "LIT", name: "Bill and Hillary Clinton", city: "Little Rock" },
  { code: "XNA", name: "Northwest Arkansas", city: "Bentonville / Fayetteville" },
  { code: "OKC", name: "Will Rogers World", city: "Oklahoma City" },
  { code: "TUL", name: "Tulsa", city: "Tulsa" },
  // --- NORTHEAST ---
  { code: "BOS", name: "Boston Logan", city: "Boston" },
  { code: "PVD", name: "Providence T.F. Green", city: "Providence" },
  { code: "BDL", name: "Hartford Bradley", city: "Hartford" },
  { code: "PWM", name: "Portland Jetport", city: "Portland, ME" },
  { code: "BGR", name: "Bangor", city: "Bangor, ME" },
  { code: "MHT", name: "Manchester-Boston Regional", city: "Manchester, NH" },
  { code: "BTV", name: "Burlington", city: "Burlington, VT" },
  { code: "SYR", name: "Syracuse Hancock", city: "Syracuse" },
  { code: "ROC", name: "Rochester", city: "Rochester, NY" },
  { code: "BUF", name: "Buffalo Niagara", city: "Buffalo" },
  { code: "ALB", name: "Albany", city: "Albany, NY" },
  { code: "PHL", name: "Philadelphia", city: "Philadelphia" },
  { code: "DCA", name: "Washington Reagan", city: "Washington DC" },
  { code: "IAD", name: "Washington Dulles", city: "Washington DC" },
  { code: "BWI", name: "Baltimore-Washington", city: "Baltimore" },
  { code: "RIC", name: "Richmond", city: "Richmond" },
  { code: "ORF", name: "Norfolk", city: "Norfolk" },
  { code: "PHF", name: "Newport News/Williamsburg", city: "Newport News" },
  { code: "CHO", name: "Charlottesville-Albemarle", city: "Charlottesville" },
  // --- WEST / MOUNTAIN ---
  { code: "SEA", name: "Seattle-Tacoma", city: "Seattle" },
  { code: "PDX", name: "Portland", city: "Portland, OR" },
  { code: "BOI", name: "Boise", city: "Boise" },
  { code: "GEG", name: "Spokane", city: "Spokane" },
  { code: "MSO", name: "Missoula", city: "Missoula" },
  { code: "BZN", name: "Bozeman Yellowstone", city: "Bozeman" },
  { code: "BIL", name: "Billings Logan", city: "Billings" },
  { code: "DEN", name: "Denver", city: "Denver" },
  { code: "COS", name: "Colorado Springs", city: "Colorado Springs" },
  { code: "ASE", name: "Aspen-Pitkin County", city: "Aspen" },
  { code: "EGE", name: "Eagle County", city: "Vail / Beaver Creek" },
  { code: "HDN", name: "Yampa Valley", city: "Steamboat Springs" },
  { code: "MTJ", name: "Montrose Regional", city: "Telluride" },
  { code: "GJT", name: "Grand Junction", city: "Grand Junction" },
  { code: "SLC", name: "Salt Lake City", city: "Salt Lake City" },
  { code: "JAC", name: "Jackson Hole", city: "Jackson, WY" },
  { code: "PHX", name: "Phoenix Sky Harbor", city: "Phoenix" },
  { code: "AZA", name: "Phoenix-Mesa Gateway", city: "Mesa" },
  { code: "TUS", name: "Tucson", city: "Tucson" },
  { code: "FLG", name: "Flagstaff", city: "Flagstaff" },
  { code: "ABQ", name: "Albuquerque", city: "Albuquerque" },
  { code: "SAF", name: "Santa Fe Regional", city: "Santa Fe" },
  { code: "LAS", name: "Las Vegas Harry Reid", city: "Las Vegas" },
  { code: "RNO", name: "Reno-Tahoe", city: "Reno" },
  // --- ALASKA / HAWAII ---
  { code: "ANC", name: "Anchorage Ted Stevens", city: "Anchorage" },
  { code: "FAI", name: "Fairbanks", city: "Fairbanks" },
  { code: "JNU", name: "Juneau", city: "Juneau" },
  { code: "HNL", name: "Honolulu Daniel K. Inouye", city: "Honolulu / Oahu" },
  { code: "OGG", name: "Maui Kahului", city: "Maui" },
  { code: "KOA", name: "Kona", city: "Big Island Kona" },
  { code: "ITO", name: "Hilo", city: "Big Island Hilo" },
  { code: "LIH", name: "Lihue", city: "Kauai" },
  // --- CANADA ---
  { code: "YYZ", name: "Toronto Pearson", city: "Toronto" },
  { code: "YTZ", name: "Toronto Billy Bishop", city: "Toronto Downtown" },
  { code: "YUL", name: "Montreal Trudeau", city: "Montreal" },
  { code: "YVR", name: "Vancouver", city: "Vancouver" },
  { code: "YYC", name: "Calgary", city: "Calgary" },
  { code: "YEG", name: "Edmonton", city: "Edmonton" },
  { code: "YOW", name: "Ottawa Macdonald-Cartier", city: "Ottawa" },
  { code: "YHZ", name: "Halifax Stanfield", city: "Halifax" },
  { code: "YWG", name: "Winnipeg", city: "Winnipeg" },
  // --- MEXICO / CARIBBEAN / CENTRAL AMERICA ---
  { code: "CUN", name: "Cancún", city: "Cancún" },
  { code: "SJD", name: "Los Cabos", city: "Cabo San Lucas" },
  { code: "PVR", name: "Puerto Vallarta", city: "Puerto Vallarta" },
  { code: "MEX", name: "Mexico City Benito Juárez", city: "Mexico City" },
  { code: "MZT", name: "Mazatlán", city: "Mazatlán" },
  { code: "CZM", name: "Cozumel", city: "Cozumel" },
  { code: "GDL", name: "Guadalajara", city: "Guadalajara" },
  { code: "MTY", name: "Monterrey", city: "Monterrey" },
  { code: "MID", name: "Mérida", city: "Mérida" },
  { code: "TLC", name: "Toluca", city: "Toluca / Mexico City" },
  { code: "OAX", name: "Oaxaca", city: "Oaxaca" },
  { code: "SJU", name: "San Juan Luis Muñoz Marín", city: "San Juan, PR" },
  { code: "STT", name: "St. Thomas Cyril E. King", city: "St. Thomas USVI" },
  { code: "STX", name: "St. Croix Henry E. Rohlsen", city: "St. Croix USVI" },
  { code: "NAS", name: "Nassau Lynden Pindling", city: "Nassau, Bahamas" },
  { code: "FPO", name: "Grand Bahama", city: "Freeport" },
  { code: "PLS", name: "Providenciales", city: "Turks & Caicos" },
  { code: "AUA", name: "Aruba Reina Beatrix", city: "Aruba" },
  { code: "CUR", name: "Curaçao Hato", city: "Curaçao" },
  { code: "SXM", name: "Sint Maarten Princess Juliana", city: "St. Maarten" },
  { code: "ANU", name: "Antigua V.C. Bird", city: "Antigua" },
  { code: "BGI", name: "Barbados Grantley Adams", city: "Barbados" },
  { code: "SLU", name: "Saint Lucia Hewanorra", city: "St. Lucia" },
  { code: "GND", name: "Grenada Maurice Bishop", city: "Grenada" },
  { code: "KIN", name: "Kingston Norman Manley", city: "Kingston" },
  { code: "MBJ", name: "Montego Bay Sangster", city: "Montego Bay" },
  { code: "POP", name: "Puerto Plata", city: "Puerto Plata, DR" },
  { code: "PUJ", name: "Punta Cana", city: "Punta Cana" },
  { code: "SDQ", name: "Santo Domingo Las Américas", city: "Santo Domingo" },
  { code: "HAV", name: "Havana José Martí", city: "Havana" },
  { code: "GCM", name: "Grand Cayman Owen Roberts", city: "Grand Cayman" },
  { code: "BZE", name: "Belize Philip S. W. Goldson", city: "Belize City" },
  { code: "SJO", name: "San José Juan Santamaría", city: "San José, Costa Rica" },
  { code: "LIR", name: "Liberia Daniel Oduber", city: "Liberia, Costa Rica" },
  { code: "PTY", name: "Panama Tocumen", city: "Panama City" },
  // --- SOUTH AMERICA ---
  { code: "BOG", name: "Bogotá El Dorado", city: "Bogotá" },
  { code: "LIM", name: "Lima Jorge Chávez", city: "Lima" },
  { code: "SCL", name: "Santiago Arturo Merino", city: "Santiago, Chile" },
  { code: "EZE", name: "Buenos Aires Ezeiza", city: "Buenos Aires" },
  { code: "GRU", name: "São Paulo Guarulhos", city: "São Paulo" },
  { code: "GIG", name: "Rio de Janeiro Galeão", city: "Rio de Janeiro" },
  { code: "UIO", name: "Quito Mariscal Sucre", city: "Quito" },
  { code: "GPS", name: "Baltra", city: "Galápagos" },
  // --- EUROPE ---
  { code: "LHR", name: "London Heathrow", city: "London" },
  { code: "LGW", name: "London Gatwick", city: "London" },
  { code: "STN", name: "London Stansted", city: "London" },
  { code: "LTN", name: "London Luton", city: "London" },
  { code: "LCY", name: "London City", city: "London" },
  { code: "MAN", name: "Manchester", city: "Manchester, UK" },
  { code: "EDI", name: "Edinburgh", city: "Edinburgh" },
  { code: "GLA", name: "Glasgow", city: "Glasgow" },
  { code: "DUB", name: "Dublin", city: "Dublin" },
  { code: "SNN", name: "Shannon", city: "Shannon, Ireland" },
  { code: "CDG", name: "Paris Charles de Gaulle", city: "Paris" },
  { code: "ORY", name: "Paris Orly", city: "Paris" },
  { code: "NCE", name: "Nice Côte d'Azur", city: "Nice" },
  { code: "MRS", name: "Marseille Provence", city: "Marseille" },
  { code: "AMS", name: "Amsterdam Schiphol", city: "Amsterdam" },
  { code: "BRU", name: "Brussels", city: "Brussels" },
  { code: "FRA", name: "Frankfurt", city: "Frankfurt" },
  { code: "MUC", name: "Munich", city: "Munich" },
  { code: "BER", name: "Berlin Brandenburg", city: "Berlin" },
  { code: "HAM", name: "Hamburg", city: "Hamburg" },
  { code: "DUS", name: "Düsseldorf", city: "Düsseldorf" },
  { code: "ZRH", name: "Zurich", city: "Zurich" },
  { code: "GVA", name: "Geneva", city: "Geneva" },
  { code: "VIE", name: "Vienna", city: "Vienna" },
  { code: "PRG", name: "Prague", city: "Prague" },
  { code: "BUD", name: "Budapest", city: "Budapest" },
  { code: "WAW", name: "Warsaw Chopin", city: "Warsaw" },
  { code: "CPH", name: "Copenhagen", city: "Copenhagen" },
  { code: "ARN", name: "Stockholm Arlanda", city: "Stockholm" },
  { code: "OSL", name: "Oslo", city: "Oslo" },
  { code: "HEL", name: "Helsinki", city: "Helsinki" },
  { code: "KEF", name: "Reykjavík Keflavík", city: "Reykjavík" },
  { code: "FCO", name: "Rome Fiumicino", city: "Rome" },
  { code: "CIA", name: "Rome Ciampino", city: "Rome" },
  { code: "MXP", name: "Milan Malpensa", city: "Milan" },
  { code: "LIN", name: "Milan Linate", city: "Milan" },
  { code: "VCE", name: "Venice Marco Polo", city: "Venice" },
  { code: "FLR", name: "Florence Peretola", city: "Florence" },
  { code: "PSA", name: "Pisa", city: "Pisa" },
  { code: "NAP", name: "Naples", city: "Naples" },
  { code: "CTA", name: "Catania Fontanarossa", city: "Catania (Sicily)" },
  { code: "PMO", name: "Palermo", city: "Palermo" },
  { code: "OLB", name: "Olbia Costa Smeralda", city: "Olbia (Sardinia)" },
  { code: "MAD", name: "Madrid Barajas", city: "Madrid" },
  { code: "BCN", name: "Barcelona El Prat", city: "Barcelona" },
  { code: "PMI", name: "Palma de Mallorca", city: "Mallorca" },
  { code: "IBZ", name: "Ibiza", city: "Ibiza" },
  { code: "AGP", name: "Málaga Costa del Sol", city: "Málaga" },
  { code: "SVQ", name: "Seville", city: "Seville" },
  { code: "VLC", name: "Valencia", city: "Valencia" },
  { code: "LIS", name: "Lisbon Humberto Delgado", city: "Lisbon" },
  { code: "OPO", name: "Porto", city: "Porto" },
  { code: "FNC", name: "Madeira Cristiano Ronaldo", city: "Madeira" },
  { code: "ATH", name: "Athens", city: "Athens" },
  { code: "JMK", name: "Mykonos", city: "Mykonos" },
  { code: "JTR", name: "Santorini Thira", city: "Santorini" },
  { code: "HER", name: "Heraklion", city: "Crete" },
  { code: "IST", name: "Istanbul", city: "Istanbul" },
  { code: "SAW", name: "Istanbul Sabiha Gökçen", city: "Istanbul" },
  // --- MIDDLE EAST / AFRICA ---
  { code: "DXB", name: "Dubai International", city: "Dubai" },
  { code: "DWC", name: "Dubai Al Maktoum", city: "Dubai" },
  { code: "AUH", name: "Abu Dhabi", city: "Abu Dhabi" },
  { code: "DOH", name: "Doha Hamad", city: "Doha" },
  { code: "TLV", name: "Tel Aviv Ben Gurion", city: "Tel Aviv" },
  { code: "AMM", name: "Amman Queen Alia", city: "Amman" },
  { code: "CAI", name: "Cairo International", city: "Cairo" },
  { code: "CMN", name: "Casablanca Mohammed V", city: "Casablanca" },
  { code: "RAK", name: "Marrakesh Menara", city: "Marrakesh" },
  { code: "CPT", name: "Cape Town", city: "Cape Town" },
  { code: "JNB", name: "Johannesburg O.R. Tambo", city: "Johannesburg" },
  { code: "NBO", name: "Nairobi Jomo Kenyatta", city: "Nairobi" },
  { code: "JRO", name: "Kilimanjaro", city: "Arusha / Kilimanjaro" },
  { code: "ZNZ", name: "Zanzibar Abeid Amani Karume", city: "Zanzibar" },
  { code: "MRU", name: "Mauritius SSR", city: "Mauritius" },
  { code: "SEZ", name: "Seychelles", city: "Mahé, Seychelles" },
  // --- ASIA / PACIFIC ---
  { code: "NRT", name: "Tokyo Narita", city: "Tokyo" },
  { code: "HND", name: "Tokyo Haneda", city: "Tokyo" },
  { code: "KIX", name: "Osaka Kansai", city: "Osaka" },
  { code: "ICN", name: "Seoul Incheon", city: "Seoul" },
  { code: "PEK", name: "Beijing Capital", city: "Beijing" },
  { code: "PKX", name: "Beijing Daxing", city: "Beijing" },
  { code: "PVG", name: "Shanghai Pudong", city: "Shanghai" },
  { code: "HKG", name: "Hong Kong", city: "Hong Kong" },
  { code: "TPE", name: "Taipei Taoyuan", city: "Taipei" },
  { code: "SIN", name: "Singapore Changi", city: "Singapore" },
  { code: "BKK", name: "Bangkok Suvarnabhumi", city: "Bangkok" },
  { code: "DMK", name: "Bangkok Don Mueang", city: "Bangkok" },
  { code: "HKT", name: "Phuket", city: "Phuket" },
  { code: "USM", name: "Koh Samui", city: "Koh Samui" },
  { code: "KUL", name: "Kuala Lumpur", city: "Kuala Lumpur" },
  { code: "CGK", name: "Jakarta Soekarno-Hatta", city: "Jakarta" },
  { code: "DPS", name: "Denpasar / Bali", city: "Bali" },
  { code: "MNL", name: "Manila Ninoy Aquino", city: "Manila" },
  { code: "CEB", name: "Cebu Mactan", city: "Cebu" },
  { code: "SGN", name: "Ho Chi Minh City Tan Son Nhat", city: "Ho Chi Minh City" },
  { code: "HAN", name: "Hanoi Noi Bai", city: "Hanoi" },
  { code: "DEL", name: "Delhi Indira Gandhi", city: "Delhi" },
  { code: "BOM", name: "Mumbai Chhatrapati Shivaji", city: "Mumbai" },
  { code: "MLE", name: "Malé Velana", city: "Maldives" },
  { code: "CMB", name: "Colombo Bandaranaike", city: "Colombo" },
  { code: "SYD", name: "Sydney Kingsford Smith", city: "Sydney" },
  { code: "MEL", name: "Melbourne Tullamarine", city: "Melbourne" },
  { code: "BNE", name: "Brisbane", city: "Brisbane" },
  { code: "PER", name: "Perth", city: "Perth" },
  { code: "CNS", name: "Cairns", city: "Cairns" },
  { code: "AKL", name: "Auckland", city: "Auckland" },
  { code: "CHC", name: "Christchurch", city: "Christchurch" },
  { code: "PPT", name: "Papeete Faa'a", city: "Tahiti" },
  { code: "NAN", name: "Nadi", city: "Fiji" },
];

export function searchAirports(query, limit = 8) {
  if (!query) return [];
  const q = query.trim().toLowerCase();
  if (!q) return [];
  return AIRPORTS.filter(
    (a) =>
      a.code.toLowerCase().startsWith(q) ||
      a.city.toLowerCase().includes(q) ||
      a.name.toLowerCase().includes(q)
  ).slice(0, limit);
}

// Common airlines (US + major international) with IATA codes for autocomplete
export const AIRLINES = [
  { code: "AA", name: "American Airlines" },
  { code: "DL", name: "Delta Air Lines" },
  { code: "UA", name: "United Airlines" },
  { code: "WN", name: "Southwest Airlines" },
  { code: "B6", name: "JetBlue" },
  { code: "AS", name: "Alaska Airlines" },
  { code: "NK", name: "Spirit Airlines" },
  { code: "F9", name: "Frontier Airlines" },
  { code: "HA", name: "Hawaiian Airlines" },
  { code: "G4", name: "Allegiant Air" },
  { code: "SY", name: "Sun Country Airlines" },
  { code: "AC", name: "Air Canada" },
  { code: "WS", name: "WestJet" },
  { code: "PD", name: "Porter Airlines" },
  { code: "AM", name: "Aeromexico" },
  { code: "Y4", name: "Volaris" },
  { code: "VB", name: "Viva Aerobus" },
  { code: "CM", name: "Copa Airlines" },
  { code: "AV", name: "Avianca" },
  { code: "LA", name: "LATAM Airlines" },
  { code: "BW", name: "Caribbean Airlines" },
  { code: "JY", name: "interCaribbean Airways" },
  { code: "BA", name: "British Airways" },
  { code: "VS", name: "Virgin Atlantic" },
  { code: "EI", name: "Aer Lingus" },
  { code: "AF", name: "Air France" },
  { code: "KL", name: "KLM Royal Dutch Airlines" },
  { code: "LH", name: "Lufthansa" },
  { code: "LX", name: "SWISS International Air Lines" },
  { code: "OS", name: "Austrian Airlines" },
  { code: "SN", name: "Brussels Airlines" },
  { code: "IB", name: "Iberia" },
  { code: "AZ", name: "ITA Airways (Alitalia)" },
  { code: "TP", name: "TAP Air Portugal" },
  { code: "TK", name: "Turkish Airlines" },
  { code: "SK", name: "SAS — Scandinavian Airlines" },
  { code: "AY", name: "Finnair" },
  { code: "LO", name: "LOT Polish Airlines" },
  { code: "OK", name: "Czech Airlines" },
  { code: "A3", name: "Aegean Airlines" },
  { code: "FR", name: "Ryanair" },
  { code: "U2", name: "easyJet" },
  { code: "W6", name: "Wizz Air" },
  { code: "DY", name: "Norwegian" },
  { code: "EK", name: "Emirates" },
  { code: "EY", name: "Etihad Airways" },
  { code: "QR", name: "Qatar Airways" },
  { code: "SV", name: "Saudia" },
  { code: "GF", name: "Gulf Air" },
  { code: "WY", name: "Oman Air" },
  { code: "MS", name: "EgyptAir" },
  { code: "ET", name: "Ethiopian Airlines" },
  { code: "KQ", name: "Kenya Airways" },
  { code: "SA", name: "South African Airways" },
  { code: "SQ", name: "Singapore Airlines" },
  { code: "CX", name: "Cathay Pacific" },
  { code: "JL", name: "Japan Airlines" },
  { code: "NH", name: "ANA — All Nippon Airways" },
  { code: "KE", name: "Korean Air" },
  { code: "OZ", name: "Asiana Airlines" },
  { code: "CI", name: "China Airlines" },
  { code: "BR", name: "EVA Air" },
  { code: "TG", name: "Thai Airways" },
  { code: "MH", name: "Malaysia Airlines" },
  { code: "GA", name: "Garuda Indonesia" },
  { code: "PR", name: "Philippine Airlines" },
  { code: "VN", name: "Vietnam Airlines" },
  { code: "AI", name: "Air India" },
  { code: "6E", name: "IndiGo" },
  { code: "QF", name: "Qantas" },
  { code: "VA", name: "Virgin Australia" },
  { code: "NZ", name: "Air New Zealand" },
  { code: "FJ", name: "Fiji Airways" },
  { code: "MU", name: "China Eastern Airlines" },
  { code: "CZ", name: "China Southern Airlines" },
  { code: "CA", name: "Air China" },
];

export function searchAirlines(query, limit = 8) {
  const q = (query || "").trim().toLowerCase();
  const src = AIRLINES;
  if (!q) return src.slice(0, limit);
  return src
    .filter((a) => a.code.toLowerCase().includes(q) || a.name.toLowerCase().includes(q))
    .slice(0, limit);
}
